import type { PlatformChallenge, PlatformSession } from '@sm/api';

/**
 * Koraci prijave operatera platforme:
 * `credentials` (e-pošta + lozinka) → `totp` (kod iz aplikacije ili rezervni kod) ili `enroll`
 * (obavezno uključivanje TOTP-a: QR + tajna, pa prvi kod) → `backupCodes` (samo poslije uključivanja,
 * prikazuju se jednom) → `done`.
 */
export type PlatformLoginState =
  | { step: 'credentials' }
  | { step: 'totp'; challenge: PlatformChallenge }
  | { step: 'enroll'; challenge: PlatformChallenge }
  | { step: 'backupCodes'; codes: string[] }
  | { step: 'done' };

export type PlatformLoginEvent =
  | { type: 'challenge'; challenge: PlatformChallenge }
  | { type: 'verified'; session: PlatformSession }
  | { type: 'codesSaved' }
  | { type: 'restart' };

export const initialLoginState: PlatformLoginState = { step: 'credentials' };

export function platformLoginReducer(
  state: PlatformLoginState,
  ev: PlatformLoginEvent,
): PlatformLoginState {
  switch (ev.type) {
    case 'challenge':
      if (state.step !== 'credentials') return state;
      return ev.challenge.status === 'totp_enrollment_required' && ev.challenge.enrollment
        ? { step: 'enroll', challenge: ev.challenge }
        : { step: 'totp', challenge: ev.challenge };
    case 'verified':
      if (state.step !== 'totp' && state.step !== 'enroll') return state;
      return ev.session.backupCodes && ev.session.backupCodes.length > 0
        ? { step: 'backupCodes', codes: ev.session.backupCodes }
        : { step: 'done' };
    case 'codesSaved':
      return state.step === 'backupCodes' ? { step: 'done' } : state;
    case 'restart':
      return initialLoginState;
  }
}

/** Sigurna putanja za povratak poslije prijave (samo unutar `/platforma`). */
export function safePlatformRedirect(redirect: string | undefined): string {
  if (
    !redirect ||
    !/^\/platforma(?:[/?#]|$)/.test(redirect) ||
    redirect.startsWith('/platforma/prijava') ||
    redirect.includes('\\') ||
    /(?:^|\/)\.{1,2}(?:\/|[?#]|$)/.test(redirect) ||
    /%/i.test(redirect)
  )
    return '/platforma';
  return redirect;
}
